import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryGroups = [
  {
    label: 'Odzież',
    items: categories.filter(c => ['shoes', 'hoodies', 't-shirts', 'jackets', 'pants', 'jersey', 'sets'].includes(c.id)),
  },
  {
    label: 'Akcesoria',
    items: categories.filter(c => ['headwear', 'accessories', 'bags', 'belts', 'perfumes'].includes(c.id)),
  },
  {
    label: 'Inne',
    items: categories.filter(c => ['underwear', 'other'].includes(c.id)),
  },
];

const articleLinks = [
  { label: 'Przewodnik Kakobuy 2026', to: '/artykul/kakobuy-spreadsheet-przewodnik-2026', icon: 'ri-book-open-line' },
  { label: 'Porównanie platform', to: '/artykul/porownanie-spreadsheetow-2026', icon: 'ri-scales-3-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleCategoryClick = () => {
    setDropdownOpen(false);
    setMobileOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link to="/" className="flex flex-col items-start leading-none">
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
                {t('nav.logo')}
              </span>
              <span className="text-xs text-foreground-400 tracking-wider">
                {t('nav.subtitle')}
              </span>
            </Link>

            {/* Center: Desktop Dropdown */}
            <div className="hidden md:flex items-center" ref={dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100/80 border border-background-200/70 text-sm text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>{t('nav.dropdown_placeholder')}</span>
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-foreground-500"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-foreground-500"></i>
                  )}
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-background-50 rounded-2xl border border-background-200/70 shadow-lg overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="max-h-80 overflow-y-auto py-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={handleCategoryClick}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                            <i className={`${cat.icon} text-sm`}></i>
                          </span>
                          <span>{t(cat.nameKey)}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: CTA + Hamburger */}
            <div className="flex items-center gap-3">
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-100/80 border border-background-200/70 text-foreground-700 cursor-pointer"
                aria-label="Otwórz menu"
              >
                <i className="ri-menu-line text-lg"></i>
              </button>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="hidden sm:inline">{t('nav.kakobuy_sheet')}</span>
                <span className="sm:hidden">Sheet</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Full-Screen Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex flex-col bg-background-50">
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/60">
            <div className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight">
                {t('nav.logo')}
              </span>
              <span className="text-xs text-foreground-400 tracking-wider">
                {t('nav.subtitle')}
              </span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-700 cursor-pointer"
              aria-label="Zamknij menu"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
            {/* Articles Section */}
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Artykuły SEO
              </h4>
              <div className="space-y-1">
                {articleLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${link.icon} text-sm`}></i>
                    </span>
                    <span>{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Categories Section */}
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Kategorie produktów
              </h4>
              {categoryGroups.map((group) => (
                <div key={group.label} className="mb-4">
                  <span className="text-xs font-medium text-foreground-500 px-3 mb-1 block">
                    {group.label}
                  </span>
                  <div className="space-y-0.5">
                    {group.items.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={handleCategoryClick}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        <span>{t(cat.nameKey)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>{t('nav.kakobuy_sheet')}</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}